// @flow

import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSortBy, useTable } from "react-table";
import { append, prop, without } from "ramda";
import { useForm } from "react-hook-form";

import { Translate, useI18n } from "@quantum/i18n";

import type {
  ArquivoMedida,
  Id,
  UsuarioCompartilhamento,
} from "../../../model";
import {
  compartilharArquivo,
  getExtraInfoOperacaoArquivos,
} from "../../../state";

import { OkButton } from "@quantum/ui";
import { IoIosArrowRoundDown, IoIosArrowRoundUp } from "react-icons/io";
import { GruposCompartilhamento } from "../../remote";
import Pagina from "../pagina";

import "./compartilhar-relatorio.css";

type TabelaProps = {
  usuarios: Array<UsuarioCompartilhamento>,
  usuariosSelecionados: Array<Id>,
  onSelecionar: (Array<Id>) => any,
};

const Cell = ({ cell: { getCellProps, value }, column: { id } }) => (
  <td
    {...getCellProps({
      className: `CompartilharRelatorio__celula CompartilharRelatorio__celula--${id}`,
    })}
  >
    {value}
  </td>
);

const MultilineCell = ({ cell: { getCellProps, value }, column: { id } }) => (
  <td
    {...getCellProps({
      className: `CompartilharRelatorio__celula CompartilharRelatorio__celula--${id}`,
    })}
  >
    {value.map((item, index) => (
      <div key={index} className="CompartilharRelatorio__item-multiline">
        {item}
      </div>
    ))}
  </td>
);

const Header = ({
  column: {
    getHeaderProps,
    getSortByToggleProps,
    header,
    isSorted,
    isSortedDesc,
  },
}) => (
  <th
    {...getHeaderProps()}
    {...getSortByToggleProps()}
    className="CompartilharRelatorio__header"
  >
    {header}
    <span className="CompartilharRelatorio__header-ordenacao">
      {isSorted ? (
        isSortedDesc ? (
          <IoIosArrowRoundDown />
        ) : (
          <IoIosArrowRoundUp />
        )
      ) : (
        ""
      )}
    </span>
  </th>
);

const TabelaUsuarios = ({
  usuarios,
  usuariosSelecionados,
  onSelecionar,
}: TabelaProps) => {
  const { translate } = useI18n();
  const columnDefs = useMemo(
    () => [
      {
        header: translate("maiusculas.nome"),
        accessor: "nome",
      },
      {
        header: translate("maiusculas.empresa"),
        accessor: "empresa",
      },
      {
        header: translate("maiusculas.logins"),
        accessor: "logins",
        Cell: MultilineCell,
      },
    ],
    [translate]
  );

  const { columns, getTableBodyProps, rows, prepareRow } = useTable(
    {
      defaultColumn: {
        Cell,
        Header,
      },
      columns: columnDefs,
      data: usuarios,
    },
    useSortBy
  );

  const onSelecionarUsuario = useCallback(
    ({ target: { id } }) =>
      usuariosSelecionados.includes(Number(id))
        ? onSelecionar(without([Number(id)], usuariosSelecionados))
        : onSelecionar(append(Number(id), usuariosSelecionados)),
    [usuariosSelecionados, onSelecionar]
  );

  const onSelecionarTodosUsuarios = useCallback(
    () =>
      usuariosSelecionados.length === usuarios.length
        ? onSelecionar([])
        : onSelecionar(usuarios.map(prop("id"))),
    [usuarios, usuariosSelecionados, onSelecionar]
  );

  return (
    <div className="CompartilharRelatorio__tabela--wrapper">
      <table
        {...getTableBodyProps({ className: "CompartilharRelatorio__tabela" })}
      >
        <thead>
          <tr className="CompartilharRelatorio__linha-header">
            <th className="CompartilharRelatorio__header CompartilharRelatorio__header--checkbox">
              <input
                type="checkbox"
                checked={usuariosSelecionados.length === usuarios.length}
                onChange={onSelecionarTodosUsuarios}
              />
            </th>

            {columns.map((column, id) => column.render("Header", { key: id }))}
          </tr>
        </thead>

        <tbody>
          {rows.map(
            (row, index) =>
              prepareRow(row) || (
                <tr
                  {...row.getRowProps({
                    className: "CompartilharRelatorio__linha",
                  })}
                >
                  <td className="CompartilharRelatorio__celula CompartilharRelatorio__celula--checkbox">
                    <input
                      id={row.original.id}
                      type="checkbox"
                      checked={usuariosSelecionados.includes(row.original.id)}
                      onChange={onSelecionarUsuario}
                    />
                  </td>

                  {row.cells.map((cell, id) =>
                    cell.render("Cell", { key: id })
                  )}
                </tr>
              )
          )}
        </tbody>
      </table>
    </div>
  );
};

const GrupoCompartilhamento = ({
  grupo,
  usuariosSelecionados,
  setUsuariosSelecionados,
}) => {
  return (
    <div className="CompartilharRelatorio__grupo">
      <h3 className="CompartilharRelatorio__nome-grupo">{grupo.nome}</h3>

      <TabelaUsuarios
        usuarios={grupo.usuarios}
        usuariosSelecionados={usuariosSelecionados}
        onSelecionar={setUsuariosSelecionados}
      />
    </div>
  );
};

const CompartilharRelatorio = () => {
  const { translate } = useI18n();
  const dispatch = useDispatch();
  const arquivoCompartilhar: ArquivoMedida = useSelector(
    getExtraInfoOperacaoArquivos
  ).arquivo;

  const [usuariosSelecionados, setUsuariosSelecionados] = useState([]);

  const { register, handleSubmit } = useForm({
    mode: "onBlur",
  });

  const aoSalvar = useCallback(
    ({ nome: nomeArquivo }) =>
      dispatch(
        compartilharArquivo({
          arquivo: arquivoCompartilhar,
          nomeArquivoCompartilhado: nomeArquivo,
          codigosUsuariosACompartilhar: usuariosSelecionados,
        })
      ),
    [dispatch, arquivoCompartilhar, usuariosSelecionados]
  );

  return (
    <Pagina titulo={translate("telas.compartilharArquivo.selecione")}>
      <form onSubmit={handleSubmit(aoSalvar)} className="CompartilharRelatorio">
        <div className="CompartilharRelatorio__nome-arquivo">
          <label className="CompartilharRelatorio__nome-arquivo-label">
            <Translate>maiusculas.nomeArquivo</Translate>
          </label>
          <input
            name="nome"
            ref={register({ required: true, minLength: 1 })}
            className="CompartilharRelatorio__nome-arquivo-input"
            type="text"
            defaultValue=""
          />
        </div>

        <GruposCompartilhamento>
          {({ data: grupos = [], loading }) =>
            grupos.map((grupo, index) => (
              <GrupoCompartilhamento
                key={index}
                grupo={grupo}
                usuariosSelecionados={usuariosSelecionados}
                setUsuariosSelecionados={setUsuariosSelecionados}
              />
            ))
          }
        </GruposCompartilhamento>

        <OkButton className="CompartilharRelatorio__salvar" type="submit">
          Ok
        </OkButton>
      </form>
    </Pagina>
  );
};

export default CompartilharRelatorio;
